import { useState } from 'react';

import { VenueDate } from '@/domain/project-venue-dates/entities/project-venue-dates.entity';
import { useDialogConfirm } from '@/ui/components/confirm-dialog/useDialogConfirm';
import { translate } from '@/ui/i18n';

export function useVenueDateSelection(
  venueDates: VenueDate[],
  onDeleteMultiple: (ids: number[]) => Promise<void>,
) {
  const dialogConfirm = useDialogConfirm();

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const allSelected =
    venueDates.length > 0 && venueDates.every((d) => selectedIds.has(d.id));

  const enterSelecting = () => setIsSelecting(true);

  const onToggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const onToggleSelectMonth = (ids: number[], monthAllSelected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (monthAllSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const onClickToggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(venueDates.map((d) => d.id)));
    }
  };

  const onClickCancelSelecting = () => {
    setIsSelecting(false);
    setSelectedIds(new Set());
  };

  const onClickDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    const response = await dialogConfirm({
      title: translate('DeleteMultipleVenueDates.Message.confirmation', {
        count: ids.length,
      }),
    });
    if (response === 'confirmed') {
      await onDeleteMultiple(ids);
      setIsSelecting(false);
      setSelectedIds(new Set());
    }
  };

  return {
    isSelecting,
    enterSelecting,
    selectedIds,
    onToggleSelect,
    onToggleSelectMonth,
    toolbarProps: {
      selectedCount: selectedIds.size,
      allSelected,
      onClickDeleteSelected,
      onClickToggleAll,
      onClickCancelSelecting,
    },
  };
}
